import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_BASE_URL } from '../../config';
import { PageLayout, Table, Button, DatePicker } from "../../components/ui";

const MemberDetails = () => {
  const [searchType, setSearchType] = useState("name");
  const [searchValue, setSearchValue] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [accountType, setAccountType] = useState("01");
  const [narration, setNarration] = useState("");
  const [selectedOption, setSelectedOption] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showTransactions, setShowTransactions] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false); // Renamed for generality
  const [transactionType, setTransactionType] = useState("deposit");
  const [amount, setAmount] = useState("");

  const today = new Date().toISOString().split("T")[0];
  const [date, setDate] = useState(today);

  // Account type labels for dynamic UI
  const accountTypeLabels = {
    "01": "Marginal Money",
    "02": "Fixed Deposit",
    "03": "Recurring Deposit",
    "04": "Loan",
    "05": "Chit",
  };

  // Fetch search results
  const fetchSearchResults = async () => {
    if (!searchValue) {
      setSearchResults([]);
      setSelectedOption(null);
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/searchByNamePhoneNoAccNoCusUniqueId`,
        { searchInput: searchValue, searchType, accountType }
      );
      const filteredResults = response.data.filter((result) => {
        if (searchType === "name") {
          return result.name?.toLowerCase().includes(searchValue.toLowerCase());
        } else if (searchType === "uniqueId") {
          return result.customerUniqueId
            ?.toLowerCase()
            .includes(searchValue.toLowerCase());
        } else if (searchType === "phone") {
          const cleanInput = searchValue.replace(/\D/g, "");
          const cleanMobile = result.mobileNumber?.toString().replace(/\D/g, "");
          return cleanMobile?.includes(cleanInput);
        }
        return true;
      });
      setSearchResults(filteredResults);
    } catch (error) {
      console.error("Error fetching search results:", error);
      alert("Error fetching search results. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Handle option selection
  const handleSelectOption = (option) => {
    setSelectedOption(option);
    setSearchValue("");
    setSearchResults([]);
    setShowTransactions(false);
    setShowAddForm(false);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const [year, month, day] = dateString.split("-");
    return `${day}-${month}-${year}`;
  };

  // Calculate closing balance
  const calculateClosingBalance = (transactions) => {
    if (!transactions || transactions.length === 0) {
      return { transactions: [], totalDeposits: 0, totalWithdrawals: 0, closingBalance: 0 };
    }
    let balance = 0;
    let totalDeposits = 0;
    let totalWithdrawals = 0;
    const formattedTransactions = transactions.map((transaction) => {
      const credit = transaction.transactionType === "deposit" ? parseFloat(transaction.amount) : 0;
      const debit = transaction.transactionType === "withdraw" ? parseFloat(transaction.amount) : 0;
      balance += credit - debit;
      totalDeposits += credit;
      totalWithdrawals += debit;
      return { ...transaction, credit, debit, closingBalance: balance };
    });
    return { transactions: formattedTransactions, totalDeposits, totalWithdrawals, closingBalance: balance };
  };

  // Fetch updated transactions
  const fetchUpdatedTransactions = async () => {
    if (!selectedOption) return;
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/searchByNamePhoneNoAccNoCusUniqueId`,
        { searchInput: selectedOption.customerUniqueId, searchType: "uniqueId", accountType }
      );
      const updatedOption = response.data.find(
        (result) => result.customerUniqueId === selectedOption.customerUniqueId
      );
      if (updatedOption) {
        setSelectedOption(updatedOption);
      }
    } catch (error) {
      console.error("Error fetching updated transactions:", error);
    }
  };

  // Handle transaction submission
  const handleSubmitTransaction = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      alert("Please enter a valid amount greater than 0.");
      return;
    }

    const payload = {
      model: "transactions",
      type: "Add",
      customerUniqueId: selectedOption.customerUniqueId,
      accountNumber: Array.isArray(selectedOption.accountNumber)
        ? selectedOption.accountNumber[0]
        : selectedOption.accountNumber || "N/A",
      transactionType,
      accountType,
      narration,
      amount: parseFloat(amount),
      date,
    };

    console.log("Submitting payload:", payload);

    setSubmitting(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/api/addDetails`, payload);
      console.log("API Response:", response.data);

      if (response.status === 200 || response.status === 201) {
        alert(`${accountTypeLabels[accountType]} transaction added successfully!`);
        setAmount("");
        setNarration("");
        setTransactionType("deposit");
        setShowAddForm(false);
        await fetchUpdatedTransactions();
        setShowTransactions(true);
      } else {
        throw new Error(`Unexpected status code: ${response.status}`);
      }
    } catch (error) {
      console.error("Error adding transaction:", error.response?.data || error.message);
      alert(`Failed to add transaction: ${error.response?.data?.error || error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  // Reset form on accountType change
  useEffect(() => {
    setShowAddForm(false);
    setShowTransactions(false);
    setSelectedOption(null);
    setSearchValue("");
    setSearchResults([]);
  }, [accountType]);

  const toolbar = (
    <div className="flex flex-row flex-wrap items-center justify-between gap-4 w-full bg-white p-4 rounded-xl border border-border-light shadow-sm">
      <div className="flex items-center gap-3 flex-wrap flex-1">
        <select
          value={searchType}
          onChange={(e) => setSearchType(e.target.value)}
          className="form-input bg-white rounded-lg border border-border-medium hover:border-primary focus:border-primary focus:ring-primary/20 text-ink-700 h-[38px] text-sm px-3 focus:outline-none transition-all"
        >
          <option value="name">Name</option>
          <option value="uniqueId">Unique ID</option>
          <option value="phone">Phone Number</option>
        </select>
        <input
          type={searchType === "phone" ? "tel" : "text"}
          value={searchValue}
          onChange={(e) => {
            const inputValue = e.target.value;
            if (searchType === "name" && /[^a-zA-Z\s]/.test(inputValue)) return;
            if (searchType === "uniqueId" && /[^a-zA-Z0-9]/.test(inputValue)) return;
            if (searchType === "phone" && /[^0-9]/.test(inputValue)) return;
            setSearchValue(inputValue);
          }}
          placeholder={`Enter ${searchType}`}
          className="form-input bg-white rounded-lg border border-border-medium hover:border-primary focus:border-primary focus:ring-primary/20 text-ink-700 h-[38px] text-sm px-3 focus:outline-none transition-all flex-1 min-w-[200px]"
        />
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <select
          value={accountType}
          onChange={(e) => setAccountType(e.target.value)}
          className="form-input bg-white rounded-lg border border-border-medium hover:border-primary focus:border-primary focus:ring-primary/20 text-ink-700 h-[38px] text-sm px-3 focus:outline-none transition-all w-[200px]"
        >
          <option value="01">Marginal Money</option>
          <option value="02">Fixed Deposit</option>
          <option value="03">Recurring Deposit</option>
          <option value="04">Loan</option>
          <option value="05">Chit</option>
        </select>
        <Button
          onClick={fetchSearchResults}
          disabled={loading}
          variant="primary"
          size="sm"
        >
          {loading ? "Searching..." : "Search"}
        </Button>
      </div>
    </div>
  );

  return (
    <PageLayout
      title="Member Details"
      subtitle="Search and view member details, account statements, and manage transactions"
      toolbar={toolbar}
    >
      {searchResults.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-bold text-ink-700 mb-2">Search Results</h3>
          <Table>
            <Table.Head>
              <tr>
                <Table.HeadCell align="left">Name</Table.HeadCell>
                <Table.HeadCell align="center">Mobile Number</Table.HeadCell>
                <Table.HeadCell align="center">Unique ID</Table.HeadCell>
              </tr>
            </Table.Head>
            <Table.Body>
              {searchResults.map((result, index) => (
                <Table.Row
                  key={index}
                  onClick={() => handleSelectOption(result)}
                  className="cursor-pointer hover:bg-primary-50 transition-colors"
                >
                  <Table.Cell align="left" className="font-semibold text-primary">{result.name || "N/A"}</Table.Cell>
                  <Table.Cell align="center">{result.mobileNumber || "N/A"}</Table.Cell>
                  <Table.Cell align="center">{result.customerUniqueId || "N/A"}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      )}

      {selectedOption && (
        <div className="flex flex-col gap-6">
          {/* Account Holder Details Card */}
          <div className="bg-white p-6 rounded-xl border border-border-light shadow-sm flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
            <div className="flex-1 flex flex-col gap-3">
              <h3 className="text-lg font-bold text-ink-900 border-b border-border-light pb-2">
                Account Holder Details
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-ink-700 mt-2">
                <p><strong>Name:</strong> {selectedOption.name || "N/A"}</p>
                <p><strong>Customer Unique ID:</strong> {selectedOption.customerUniqueId || "N/A"}</p>
                <p>
                  <strong>Account Number:</strong>{" "}
                  {selectedOption.accountNumber?.length > 0
                    ? selectedOption.accountNumber.join(", ")
                    : "N/A"}
                </p>
                <p><strong>Address:</strong> {selectedOption.permanentStreetAddress || "N/A"}</p>
              </div>
            </div>
            {selectedOption.customerProfileImage && (
              <div className="flex-shrink-0">
                <img
                  src={`${API_BASE_URL}/images/${selectedOption.customerProfileImage}`}
                  alt="Customer Profile"
                  onError={(e) => (e.target.src = "/default-profile.png")}
                  className="w-32 h-32 object-cover rounded-xl border-2 border-border-medium shadow-sm"
                />
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button
              variant={showTransactions ? "secondary" : "primary"}
              onClick={() => setShowTransactions(!showTransactions)}
            >
              {showTransactions ? `Hide ${accountTypeLabels[accountType]}` : `View ${accountTypeLabels[accountType]}`}
            </Button>
            <Button
              variant={showAddForm ? "secondary" : "success"}
              onClick={() => setShowAddForm(!showAddForm)}
            >
              {showAddForm ? "Cancel" : `Add ${accountTypeLabels[accountType]}`}
            </Button>
          </div>

          {/* Transactions */}
          {showTransactions && (
            <div className="flex flex-col gap-4">
              <h3 className="text-base font-bold text-ink-900 border-b border-border-light pb-2">
                {accountTypeLabels[accountType]} Transactions
              </h3>
              {selectedOption.fk_personal_details_hasMany_transactions_customerUniqueId ? (
                (() => {
                  const { transactions, totalDeposits, totalWithdrawals, closingBalance } =
                    calculateClosingBalance(
                      selectedOption.fk_personal_details_hasMany_transactions_customerUniqueId
                    );
                  return (
                    <div className="flex flex-col gap-4">
                      {/* Summary Cards */}
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div className="bg-emerald-50 border border-emerald-200 p-4 rounded-xl flex flex-col gap-1">
                          <span className="text-[10px] font-bold text-emerald-800 uppercase tracking-wider">Total Deposits</span>
                          <span className="text-lg font-bold text-emerald-700">₹{totalDeposits.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                        </div>
                        <div className="bg-red-50 border border-red-200 p-4 rounded-xl flex flex-col gap-1">
                          <span className="text-[10px] font-bold text-red-800 uppercase tracking-wider">Total Withdrawals</span>
                          <span className="text-lg font-bold text-red-700">₹{totalWithdrawals.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                        </div>
                        <div className="bg-sky-50 border border-sky-200 p-4 rounded-xl flex flex-col gap-1">
                          <span className="text-[10px] font-bold text-sky-800 uppercase tracking-wider">Closing Balance</span>
                          <span className="text-lg font-bold text-sky-700">₹{closingBalance.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                        </div>
                      </div>

                      <Table>
                        <Table.Head>
                          <tr>
                            <Table.HeadCell align="center">Date</Table.HeadCell>
                            <Table.HeadCell align="center">Transaction ID</Table.HeadCell>
                            <Table.HeadCell align="center">Account Number</Table.HeadCell>
                            <Table.HeadCell align="left">Description</Table.HeadCell>
                            <Table.HeadCell align="right">Credit</Table.HeadCell>
                            <Table.HeadCell align="right">Debit</Table.HeadCell>
                            <Table.HeadCell align="right">Closing Balance</Table.HeadCell>
                          </tr>
                        </Table.Head>
                        <Table.Body>
                          {transactions.map((transaction, index) => (
                            <Table.Row key={index}>
                              <Table.Cell align="center">{formatDate(transaction.date)}</Table.Cell>
                              <Table.Cell align="center" className="font-medium">{transaction.transactionId || "N/A"}</Table.Cell>
                              <Table.Cell align="center">{transaction.accountNumber || "N/A"}</Table.Cell>
                              <Table.Cell align="left">{transaction.narration || "N/A"}</Table.Cell>
                              <Table.Cell align="right" className="text-emerald-600 font-medium">
                                {transaction.credit ? `₹${transaction.credit.toFixed(2)}` : "-"}
                              </Table.Cell>
                              <Table.Cell align="right" className="text-red-600 font-medium">
                                {transaction.debit ? `₹${transaction.debit.toFixed(2)}` : "-"}
                              </Table.Cell>
                              <Table.AmountCell align="right">
                                {transaction.closingBalance}
                              </Table.AmountCell>
                            </Table.Row>
                          ))}
                        </Table.Body>
                      </Table>
                    </div>
                  );
                })()
              ) : (
                <div className="bg-white p-6 rounded-xl border border-border-light text-center text-ink-500">
                  No transactions available for {accountTypeLabels[accountType]}.
                </div>
              )}
            </div>
          )}

          {/* Add Form */}
          {showAddForm && (
            <div className="bg-white p-6 rounded-xl border border-border-light shadow-sm flex flex-col gap-4 max-w-lg mx-auto w-full mt-4">
              <h3 className="text-base font-bold text-ink-900 border-b border-border-light pb-2 text-center">
                Add {accountTypeLabels[accountType]} Transaction
              </h3>
              
              <div className="flex flex-col gap-4 mt-2">
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-ink-700">Transaction Type</label>
                  <select
                    value={transactionType}
                    onChange={(e) => setTransactionType(e.target.value)}
                    className="form-input bg-white rounded-lg border border-border-medium hover:border-primary focus:border-primary focus:ring-primary/20 text-ink-700 h-[38px] text-sm px-3 focus:outline-none transition-all"
                  >
                    <option value="deposit">Credit</option>
                    <option value="withdraw">Debit</option>
                  </select>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-ink-700">Amount</label>
                  <input
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="Enter Amount"
                    min="0.01"
                    step="0.01"
                    onWheel={(e) => e.target.blur()}
                    className="form-input bg-white rounded-lg border border-border-medium hover:border-primary focus:border-primary focus:ring-primary/20 text-ink-700 h-[38px] text-sm px-3 focus:outline-none transition-all"
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-ink-700">Narration</label>
                  <input
                    type="text"
                    value={narration}
                    onChange={(e) => setNarration(e.target.value)}
                    placeholder="Enter narration"
                    className="form-input bg-white rounded-lg border border-border-medium hover:border-primary focus:border-primary focus:ring-primary/20 text-ink-700 h-[38px] text-sm px-3 focus:outline-none transition-all"
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-ink-700">Select Date</label>
                  <DatePicker
                    selected={date}
                    onChange={(dateObj) => setDate(dateObj ? dateObj.toISOString().split('T')[0] : '')}
                    placeholder="DD-MM-YYYY"
                  />
                </div>

                <div className="flex justify-end gap-3 mt-2">
                  <Button
                    onClick={handleSubmitTransaction}
                    disabled={submitting}
                    variant="primary"
                    className="w-full"
                  >
                    {submitting ? "Submitting..." : "Submit"}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </PageLayout>
  );
};

export default MemberDetails;
